import { createRouter, createWebHistory } from 'vue-router';
import { basicRoutes, permissionRoutes } from './routes';
import { usePermissionStore } from '@/stores/Permission';
import { PermissionType, getAllPermissionTypes } from './types';
import MainLayout from '@/Layout/MainLayout.vue';

const RawMainLayout = markRaw(MainLayout);

const processedBasicRoutes = basicRoutes.map(route => {
  if (route.component === MainLayout) {
    return { ...route, component: RawMainLayout };
  }
  if (route.children) {
    return {
      ...route,
      children: route.children.map(child =>
        child.component === MainLayout
          ? { ...child, component: RawMainLayout }
          : child
      )
    };
  }
  return route;
});

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: processedBasicRoutes
});

// 标记动态路由是否已注入（模块级变量，刷新时随模块重载自动重置）
let isRoutesAdded = false;

/**
 * 深拷贝路由配置树，保留 component 函数引用，避免：
 * 1. addRoute 时传入 Pinia 响应式代理对象
 * 2. processRouteComponent 修改原始 permissionRoutes
 */
function cloneRouteTree(routes: any[]): any[] {
  return routes.map(route => {
    const cloned: any = { ...route };
    if (cloned.meta) cloned.meta = { ...cloned.meta };
    if (cloned.children) {
      cloned.children = cloneRouteTree(cloned.children);
    }
    return cloned;
  });
}

/**
 * 标记路由组件为 markRaw，防止 Vue 响应式追踪组件对象
 */
function processRouteComponent(route: any): any {
  if (route.component && !route.component.__v_skip) {
    route.component = markRaw(route.component);
  }
  if (route.children && route.children.length > 0) {
    route.children = route.children.map((child: any) => processRouteComponent(child));
  }
  return route;
}

router.beforeEach(async (to, from, next) => {
  if (to.path === '/login') {
    next();
    return;
  }

  const userInfoStr = sessionStorage.getItem('userInfo');
  if (!userInfoStr) {
    next('/login');
    return;
  }

  let userData;
  try {
    userData = JSON.parse(userInfoStr);
  } catch (error) {
    console.error('解析用户信息失败:', error);
    next('/login');
    return;
  }

  const permissionStore = usePermissionStore();

  if (!isRoutesAdded) {
    let userPermissions: PermissionType[] = [];

    if (userData.schoolId === null) {
      userPermissions = getAllPermissionTypes();
    } else {
      userPermissions = [
        PermissionType.SchoolManage,
        PermissionType.UserEvaluation,
        PermissionType.StatisticHome
      ];
    }

    permissionStore.setPermissions(userPermissions);

    // 关键：从原始 permissionRoutes 深拷贝一份全新的路由树注入 router
    // 不从 Pinia store 取（避免响应式代理传入 addRoute）
    const routesToAdd = cloneRouteTree(permissionRoutes);
    routesToAdd.forEach(route => {
      router.addRoute(processRouteComponent(route));
    });

    isRoutesAdded = true;

    // 用当前完整路径重新触发导航，使新路由表生效
    next({ path: to.path, query: to.query, hash: to.hash, replace: true });
    return;
  }

  const requiredPermission = to.meta.permission;
  if (requiredPermission) {
    const hasPermission = Array.isArray(requiredPermission)
      ? requiredPermission.some(perm => permissionStore.permissions.includes(perm))
      : permissionStore.permissions.includes(requiredPermission);

    if (!hasPermission) {
      next('/403');
      return;
    }
  }

  next();
});

export default router;
